import {
  useAuthoredRows,
  useCreateAuthoredRow,
  useDeleteAuthoredRow,
  useUpdateAuthoredRow,
} from 'components/IAP/hooks/icsAuthoredFormHooks';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { OperationalPeriodScheduleControl } from '../OperationalPeriodScheduleControl';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { PrintedValue } from '../icsBoxPrimitives';
import SaveableField from '../SaveableField';
import {
  ICS236_BOX_LABELS,
  ICS236_RESOURCE_COLUMNS,
  ICS236_SURPLUS_SENTENCE,
  ICS236_TO_VALUE,
} from '../ics236BoxLabels';
import {
  BoxRowTable,
  NewCgFormCard,
  NewCgHeaderBoxes,
  NewCgPreparedByBox,
} from './newCgFormBoxes';

/**
 * ICS 236-CG, boxed as the sheet prints it.
 *
 * This sheet dates itself rather than naming an operational period, so box 3
 * takes the place the header's period box holds on the rest of the set.
 */
const Ics236Card = () => {
  const { effectivePeriodNumber } = useICSContext();
  const { data: headers = [] } = useAuthoredRows(
    'tentativeReleases',
    effectivePeriodNumber
  );
  const { data: items = [] } = useAuthoredRows(
    'tentativeReleaseItems',
    effectivePeriodNumber
  );
  const createHeader = useCreateAuthoredRow('tentativeReleases');
  const updateHeader = useUpdateAuthoredRow('tentativeReleases');
  const createItem = useCreateAuthoredRow('tentativeReleaseItems');
  const updateItem = useUpdateAuthoredRow('tentativeReleaseItems');
  const deleteItem = useDeleteAuthoredRow('tentativeReleaseItems');
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_236',
    effectivePeriodNumber
  );
  const onSuccess = () => recordVersionSave.mutate();
  const header = headers[0] ?? null;

  const saveHeader = (patch: Record<string, unknown>) => {
    if (!header) {
      createHeader.mutate(
        { operational_period_number: effectivePeriodNumber, ...patch },
        { onSuccess }
      );
      return;
    }
    updateHeader.mutate(
      { entityId: String(header.release_id), ...patch },
      { onSuccess }
    );
  };

  return (
    <div data-tour="demob-ics236">
    <NewCgFormCard
      title="Tentative Release List ICS-236"
      formname="ICS_236_CG"
      baseId="ICS_236"
      exportLabel="Preview ICS-236"
    >
      <NewCgHeaderBoxes
        labels={ICS236_BOX_LABELS}
        testIdPrefix="ics236"
        trailing={
          <Ics204CardSection
            boxLabel={ICS236_BOX_LABELS.date}
            testId="ics236-section-date"
            className="tw:mt-0 tw:h-full tw:min-w-0"
          >
            <OperationalPeriodScheduleControl testId="ics236-field-date" />
          </Ics204CardSection>
        }
      />

      <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
        <Ics204CardSection
          boxLabel={ICS236_BOX_LABELS.from}
          testId="ics236-section-from"
          className="tw:mt-0 tw:h-full tw:min-w-0"
        >
          <SaveableField
            id="ics236-from"
            label="From"
            hideLabel
            placeholder="Section Chief or Command Staff Officer"
            value={String(header?.from_section ?? '')}
            onSave={(value) => saveHeader({ from_section: value })}
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS236_BOX_LABELS.to}
          testId="ics236-section-to"
          className="tw:mt-0 tw:h-full tw:min-w-0"
        >
          <PrintedValue value={ICS236_TO_VALUE} testId="ics236-field-to" />
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={ICS236_BOX_LABELS.surplusResources}
        testId="ics236-section-surplus"
        className="tw:mt-0"
      >
        <p className="tw:mb-2 tw:text-xs tw:text-muted-foreground">
          {ICS236_SURPLUS_SENTENCE}
        </p>
        <SaveableField
          id="ics236-surplus-as-of"
          label="Surplus as of (time and date)"
          placeholder="e.g. 1800 on 03/14/2026"
          value={String(header?.surplus_as_of ?? '')}
          onSave={(value) => saveHeader({ surplus_as_of: value })}
        />
        <div className="tw:mt-3">
          <BoxRowTable
            columns={ICS236_RESOURCE_COLUMNS}
            rows={items}
            identityColumn="item_id"
            testId="ics236-resources-table"
            addLabel="Add Surplus Item"
            onSave={(entityId, patch) =>
              updateItem.mutate({ entityId, ...patch }, { onSuccess })
            }
            onDelete={(entityId) => deleteItem.mutate(entityId, { onSuccess })}
            onAdd={async () => {
              const created = await createItem.mutateAsync({
                operational_period_number: effectivePeriodNumber,
              });
              onSuccess();
              return String(created?.item_id ?? '');
            }}
          />
        </div>
      </Ics204CardSection>

      <NewCgPreparedByBox
        baseId="ICS_236"
        boxLabel={ICS236_BOX_LABELS.preparedBy}
        testIdPrefix="ics236"
      />
    </NewCgFormCard>
    </div>
  );
};

export default Ics236Card;
